/**
 * Recently-viewed products — first-party, local-only.
 *
 * Design constraints
 * ------------------
 *   * Purely `localStorage` — no cookies, no consent-affecting storage,
 *     no network calls, no PII.
 *   * Records only the product slug and a compact display snapshot
 *     (name, image, format, price) so a re-visiting shopper can hop back
 *     to a compound without another API round-trip.
 *   * Deduped by slug (most-recently-viewed wins).
 *   * Capped at 12 entries and 60-day TTL — older entries drop off.
 *   * Never surfaces items already in cart (that logic lives in the
 *     rail component that calls `readRecentlyViewed`).
 *
 * The rail is optional and dismissible; a shopper with no viewing
 * history sees no rail at all.
 */

const KEY = "mps_recently_viewed_v1";
const MAX_ITEMS = 12;
const TTL_MS = 60 * 24 * 60 * 60 * 1000; // 60 days

const isBrowser = () => typeof window !== "undefined" && !!window.localStorage;

function safeParse(raw) {
  try { return JSON.parse(raw) || []; } catch { return []; }
}

/** Read a de-duplicated, expiry-filtered list, newest-first. */
export function readRecentlyViewed() {
  if (!isBrowser()) return [];
  const raw = window.localStorage.getItem(KEY);
  if (!raw) return [];
  const list = safeParse(raw);
  const cutoff = Date.now() - TTL_MS;
  return list.filter((r) => r && r.slug && (r._at || 0) >= cutoff);
}

/**
 * Record a product view.  Idempotent — a repeat view within the same
 * millisecond is a silent no-op.  We take only the fields we render;
 * every other field is dropped so this stays lean.
 */
export function recordProductView(product) {
  if (!isBrowser() || !product?.slug) return;
  const snapshot = {
    slug:                 product.slug,
    public_display_name:  product.public_display_name || product.product_name || "",
    product_name:         product.product_name || "",
    strength:             product.strength || "",
    format:               product.format || "",
    public_sku:           product.public_sku || "",
    retail_price_cents:   product.retail_price_cents,
    public_price_visible: product.public_price_visible !== false,
    thumbnail_image_url:  product.thumbnail_image_url || product.primary_image_url || "",
    image_alt_text:       product.image_alt_text || "",
    active:               product.active,
    ecommerce_enabled:    product.ecommerce_enabled,
    category_slug:        product.category_slug || product.category || "",
    _at: Date.now(),
  };
  const prev = readRecentlyViewed().filter((r) => r.slug !== snapshot.slug);
  const next = [snapshot, ...prev].slice(0, MAX_ITEMS);
  try { window.localStorage.setItem(KEY, JSON.stringify(next)); } catch { /* no-op */ }
}

/** Remove a slug from the list — used by the row's per-item dismiss. */
export function forgetProductView(slug) {
  if (!isBrowser() || !slug) return;
  const next = readRecentlyViewed().filter((r) => r.slug !== slug);
  try { window.localStorage.setItem(KEY, JSON.stringify(next)); } catch { /* no-op */ }
}

/** Clear the entire row — used by the "clear all" affordance. */
export function clearRecentlyViewed() {
  if (!isBrowser()) return;
  try { window.localStorage.removeItem(KEY); } catch { /* no-op */ }
}
